import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Network, Send } from 'lucide-react';
import { Button } from '@tark/shared/components';
import {
  useGenerateOrganizerCompetitionDraw,
  useOrganizerCompetitionLaunchReadiness,
  useOrganizerCompetitionDraw,
  usePublishOrganizerCompetitionDraw,
} from '@tark/shared/hooks';
import { getErrorMessage, isApiError, type CompetitionDrawDivision } from '@tark/shared/lib';
import OrganizerSchedulePanel from './OrganizerSchedulePanel';
import OrganizerResultsPanel from './OrganizerResultsPanel';

export default function OrganizerDrawPanel({
  competitionPublicId,
  entrantsLocked,
  canAdmin,
  view = 'draw',
}: {
  competitionPublicId: string;
  entrantsLocked: boolean;
  canAdmin: boolean;
  view?: 'draw' | 'schedule' | 'results' | 'standings';
}) {
  const draw = useOrganizerCompetitionDraw(competitionPublicId, true);
  const readiness = useOrganizerCompetitionLaunchReadiness(competitionPublicId, true);
  const generate = useGenerateOrganizerCompetitionDraw(competitionPublicId);
  const publish = usePublishOrganizerCompetitionDraw(competitionPublicId);
  const [divisionId, setDivisionId] = useState('');
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const divisions = draw.data?.divisions ?? [];

  useEffect(() => {
    if (!divisions.some((division) => division.divisionPublicId === divisionId)) {
      setDivisionId(divisions[0]?.divisionPublicId ?? '');
    }
  }, [divisionId, divisions]);

  const selected = useMemo(
    () => divisions.find((division) => division.divisionPublicId === divisionId) ?? divisions[0],
    [divisionId, divisions],
  );
  const pending = generate.isPending || publish.isPending;
  const state = draw.data?.status ?? 'NOT_GENERATED';
  const published = state === 'PUBLISHED';
  const draft = state === 'DRAFT';

  const run = async (action: 'generate' | 'publish') => {
    if (!draw.data) return;
    setMessage(null);
    try {
      const request = {
        clientMutationId: createMutationId(),
        expectedCompetitionVersion: draw.data.competitionVersion,
      };
      if (action === 'generate') await generate.mutateAsync(request);
      else await publish.mutateAsync(request);
      setMessage({
        tone: 'success',
        text: action === 'generate' ? 'Draw prepared for review.' : 'Draw published to players.',
      });
      setConfirmPublish(false);
    } catch (error) {
      setMessage({ tone: 'error', text: drawErrorMessage(error) });
      void draw.refetch();
    }
  };

  return (
    <section className="tk-panel p-4" aria-labelledby="competition-draw-title">
      <div hidden={view !== 'draw'}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Network className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="competition-draw-title" className="text-lg font-semibold text-text">Round-robin draw</h2>
              <StatusPill status={state} />
            </div>
            <p className="mt-1 text-sm text-text-muted">{drawHint(state, entrantsLocked)}</p>
          </div>
        </div>

        {canAdmin && entrantsLocked && draw.data && !published && !confirmPublish ? (
          <Button
            leftIcon={pending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : draft ? <Send className="h-4 w-4" /> : <Network className="h-4 w-4" />}
            disabled={pending || readiness.data?.ready !== true}
            title={readiness.data?.ready === false ? 'Complete the launch check before preparing or publishing the draw' : undefined}
            onClick={() => draft ? setConfirmPublish(true) : void run('generate')}
          >
            {pending ? 'Working...' : draft ? 'Publish draw' : 'Prepare draw'}
          </Button>
        ) : null}
      </div>

      {confirmPublish ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-l-2 border-primary pl-3">
          <p className="text-sm font-medium text-text">
            Publish these matchups to every registered player?
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={pending} onClick={() => setConfirmPublish(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              leftIcon={pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              disabled={pending || readiness.data?.ready !== true}
              onClick={() => void run('publish')}
            >
              {pending ? 'Publishing...' : 'Publish draw'}
            </Button>
          </div>
        </div>
      ) : null}

      {message ? (
        <div
          role={message.tone === 'error' ? 'alert' : 'status'}
          className={`mt-4 flex items-center gap-2 text-sm font-medium ${message.tone === 'error' ? 'text-red-600' : 'text-emerald-700'}`}
        >
          {message.tone === 'error'
            ? <AlertCircle className="h-4 w-4" />
            : <CheckCircle2 className="h-4 w-4" />}
          {message.text}
        </div>
      ) : null}

      {draw.isLoading ? (
        <div className="mt-5 flex min-h-24 items-center justify-center gap-2 text-sm text-text-muted">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Loading draw
        </div>
      ) : draw.isError ? (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-l-2 border-red-500 pl-3">
          <p className="text-sm text-text-muted">The draw could not be loaded.</p>
          <Button size="sm" variant="outline" onClick={() => void draw.refetch()}>Retry</Button>
        </div>
      ) : selected ? (
        <DrawPreview
          divisions={divisions}
          selected={selected}
          onSelect={setDivisionId}
        />
      ) : null}

      </div>
      {view !== 'draw' && !published && <p className="text-sm text-text-muted">Publish the draw to open {view === 'schedule' ? 'match scheduling' : view}.</p>}
      <div hidden={view !== 'schedule'}>
      <OrganizerSchedulePanel
        competitionPublicId={competitionPublicId}
        published={published}
        canAdmin={canAdmin}
      />
      </div>
      <div hidden={!['results', 'standings'].includes(view)}>
      <OrganizerResultsPanel
        competitionPublicId={competitionPublicId}
        published={published}
        canAdmin={canAdmin}
        displayMode={view === 'standings' ? 'leaderboard' : 'results'}
      />
      </div>
    </section>
  );
}

function DrawPreview({
  divisions,
  selected,
  onSelect,
}: {
  divisions: CompetitionDrawDivision[];
  selected: CompetitionDrawDivision;
  onSelect: (divisionPublicId: string) => void;
}) {
  return (
    <div className="mt-5 space-y-4">
      {divisions.length > 1 ? (
        <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Draw divisions">
          {divisions.map((division) => (
            <button
              key={division.divisionPublicId}
              type="button"
              role="tab"
              aria-selected={division.divisionPublicId === selected.divisionPublicId}
              className={`shrink-0 border-b-2 px-1 py-2 text-sm font-semibold transition-colors ${division.divisionPublicId === selected.divisionPublicId ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text'}`}
              onClick={() => onSelect(division.divisionPublicId)}
            >
              {division.divisionName}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap items-baseline gap-2">
          <p className="font-semibold text-text">{selected.divisionName}</p>
          <p className="text-xs font-semibold text-text-muted">{formatCode(selected.formatCode)}</p>
        </div>
      )}

      <div className="grid gap-5 overflow-x-auto pb-2 md:grid-flow-col md:auto-cols-[minmax(230px,1fr)]">
        {selected.rounds.map((round) => (
          <div key={round.roundNumber} className="min-w-0 rounded-lg border border-border bg-surface p-3">
            <p className="mb-3 text-xs font-semibold uppercase text-text-muted">Round {round.roundNumber}</p>
            <div className="space-y-3">
              {round.matches.map((match) => (
                <div key={match.matchPublicId ?? `${round.roundNumber}-${match.matchNumber}`} className="rounded-md border border-border bg-background p-3">
                  <p className="text-sm font-semibold text-text">{sideNames(match.sideA.displayNames)}</p>
                  <p className="my-1 text-xs font-semibold text-text-muted">vs</p>
                  <p className="text-sm font-semibold text-text">{sideNames(match.sideB.displayNames)}</p>
                </div>
              ))}
              {selected.byes.filter((bye) => bye.roundNumber === round.roundNumber).map((bye) => (
                <p key={`${round.roundNumber}-${bye.side.entryPublicId ?? sideNames(bye.side.displayNames)}`} className="text-sm text-text-muted">
                  <span className="font-semibold text-text">{sideNames(bye.side.displayNames)}</span> · Bye
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const label = status === 'PUBLISHED' ? 'Published' : status === 'DRAFT' ? 'Ready to review' : 'Not prepared';
  const style = status === 'PUBLISHED'
    ? 'bg-emerald-500/10 text-emerald-700'
    : status === 'DRAFT' ? 'bg-amber-500/10 text-amber-700' : 'bg-surface-elevated text-text-muted';
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${style}`}>{label}</span>;
}

function drawHint(status: string, entrantsLocked: boolean): string {
  if (status === 'PUBLISHED') return 'Players can now see their rounds and opponents.';
  if (status === 'DRAFT') return 'Check each round, then publish once the field looks right.';
  if (!entrantsLocked) return 'Lock the final entrant field before preparing matchups.';
  return 'Create every matchup once with balanced rounds and automatic byes.';
}

function formatCode(value: string): string {
  return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (letter: string) => letter.toUpperCase());
}

function sideNames(names: string[]): string {
  return names.filter(Boolean).join(' / ') || 'Player unavailable';
}

function createMutationId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function drawErrorMessage(error: unknown): string {
  if (isApiError(error) && error.response?.data.code === 'ORGANIZER_EVENT_QUOTA_EXHAUSTED') return 'Your free event allowance is used. Contact TARK for additional events.';
  const message = getErrorMessage(error).toLowerCase();
  if (message.includes('version') || message.includes('changed') || message.includes('stale')) {
    return 'The competition changed. Refresh and review the latest field.';
  }
  if (message.includes('lock')) return 'Lock every division before preparing the draw.';
  if (message.includes('format')) return 'This release supports singles and fixed-pair doubles.';
  if (message.includes('approved') || message.includes('field')) return 'Each division needs at least two valid approved entries.';
  return 'The draw action could not be completed. Refresh and try again.';
}
